/**
 * /step-up — Surface Layer (/01)
 *
 * Fragile-session interstitial (Auth Flow v2). A password-only login NEVER
 * grants data access (the BFF answers 403 `step_up_required` everywhere);
 * this screen confirms identity by e-mail: it sends a one-time sign-in link
 * (registered e-mails only) and the link re-enters through the security
 * ceremony — new password, then passkey update — before the app.
 */

import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, InlineNotification, Stack, Theme } from '@carbon/react';
import { MailAll } from '@carbon/icons-react';
import { sendMagicLink } from '@auth-provider';
import { useAuthStore } from '@auth/auth-store';
import { isFragileSession } from '@auth/session-strength';
import { useSurfaceSession } from '@auth/useSurfaceSession';
import './sign-in.scss';

export const Route = createFileRoute('/step-up')({
  component: StepUpPage,
});

function StepUpPage() {
  const navigate = useNavigate();
  const { signOut } = useSurfaceSession();
  const email = useAuthStore((s) => s.identity?.email ?? '');
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const autoSentRef = useRef(false);

  async function sendLink() {
    if (!email || busy) return;
    setBusy(true);
    setSendError(null);
    try {
      const result = await sendMagicLink(email);
      if (result.error) {
        setSendError(result.error.message);
      } else {
        setSent(true);
      }
    } finally {
      setBusy(false);
    }
  }

  // Auto-send once on arrival (the whole point of the interstitial).
  useEffect(() => {
    if (autoSentRef.current || !email) return;
    autoSentRef.current = true;
    void sendLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  // Direct navigation without a fragile session → back to sign-in.
  useEffect(() => {
    if (!isFragileSession() && !email) void navigate({ to: '/sign-in' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function switchAccount() {
    await signOut();
    void navigate({ to: '/sign-in' });
  }

  return (
    <Theme theme="g100">
      <main className="sign-in-page">
        <section className="sign-in-card" aria-labelledby="step-up-heading">
          <MailAll size={32} aria-hidden="true" className="sign-in-card__icon" />
          <h1 id="step-up-heading" className="sign-in-card__heading">
            Confirm it&apos;s you
          </h1>
          <p className="sign-in-card__subtitle">
            Password sign-in is a verification entry only — it does not unlock your workspace.
            We sent a one-time link to <strong>{email || 'your e-mail'}</strong>. Open it to set a
            new password and refresh your passkey.
          </p>

          {sent ? (
            <InlineNotification
              kind="success"
              lowContrast
              hideCloseButton
              title="Check your e-mail"
              subtitle="Open the sign-in link to continue. You can close this tab."
            />
          ) : null}
          {sendError ? (
            <InlineNotification
              kind="error"
              lowContrast
              hideCloseButton
              title="Could not send the link"
              subtitle={sendError}
            />
          ) : null}

          <Stack gap={5}>
            <Button kind="tertiary" disabled={busy || !email} onClick={() => void sendLink()}>
              {busy ? 'Sending…' : sent ? 'Resend the link' : 'Send the link'}
            </Button>
            <Button kind="ghost" onClick={() => void switchAccount()}>
              Use a different account
            </Button>
          </Stack>
        </section>
      </main>
    </Theme>
  );
}
